import { SxProp, View } from 'dripsy';

import React from 'react';

import { AmountSummaryList } from '../../cards/AmountSummaryList';
import { H5 } from '@/design-system/components/typography/UniTypography';
import { AnalyticRange, Analytics } from './useMyEarnings.hook';
import { MyEarningsBarChart } from './MyEarningsBarChart';
import { UniSlideRadioButton } from '@/design-system/components/buttons/slideRadio/UniSlideRadioButton';
import { AxisItem } from '@/design-system/components/charts/axis/useAxis.hook';
import { PageItemSkeleton } from '../../PageItemSkeleton';
import { useTranslation } from 'react-i18next';
import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { useDimensions } from '@/devtools/hooks/useDimensions';

export interface MyEarningsProps {
  overview?: Analytics['analytics'];
  barChartData: Array<AxisItem>;
  showAmount: boolean;
  currency: string;
  loading: boolean;
  analyticRange: AnalyticRange;
  changeDateRange: (value: AnalyticRange) => void;
}

export const MyEarnings: React.FC<MyEarningsProps> = ({ overview, barChartData, showAmount, currency, loading, changeDateRange }) => {
  const { t } = useTranslation();

  const { dimensions, onLayout } = useDimensions({});

  return (
    <View sx={styles.container}>
      <View sx={styles.containerHeader}>
        <H5 bold>{t('earn-invest::glossary.my-earnings')}</H5>
        <View sx={styles.buttonGroup}>
          <SegmentSelect
            key="overview-page-item-header-suffix-0"
            data={[
              {
                labelKey: 'earn-invest::glossary.seven-days',
                value: AnalyticRange.$7Day
              },
              {
                labelKey: 'earn-invest::glossary.thirty-days',
                value: AnalyticRange.$30Day
              },
              {
                labelKey: 'earn-invest::glossary.ninety-days',
                value: AnalyticRange.$90Day
              }
            ]}
            onValueChange={changeDateRange}
            buttonWidth={104}
          />
        </View>
      </View>
      <View sx={styles.containerTop}>
        <AmountSummaryList
          data={[
            {
              label: t('earn-invest::glossary.yesterday'),
              asset: currency,
              value: overview?.$1d?.toString() || ' - ',
              success: true
            },
            {
              label: t('earn-invest::glossary.seven-days'),
              asset: currency,
              value: overview?.$7d?.toString() || ' - ',
              success: true
            },
            {
              label: t('earn-invest::glossary.thirty-days'),
              asset: currency,
              value: overview?.$30d?.toString() || ' - ',
              success: true
            }
          ]}
          gap="$10"
          showValue={showAmount}
        />
      </View>
      <View sx={styles.containerBottom} onLayout={onLayout}>
        {loading && <PageItemSkeleton />}
        {!loading && <MyEarningsBarChart data={barChartData} height={dimensions.height - 12} />}
      </View>
    </View>
  );
};

enum StyleName {
  chart = 'chart',
  container = 'container',
  containerHeader = 'containerHeader',
  containerTop = 'containerTop',
  containerBottom = 'containerBottom',
  summaryContainer = 'summaryContainer',
  buttonGroup = 'buttonGroup',
  button = 'button'
}

const styles: Record<StyleName, SxProp> = {
  chart: {
    mt: '$5'
  },
  container: {
    flex: 1
  },
  containerHeader: {
    mb: '$10',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between'
  },
  containerTop: {
    flex: 1,
    mb: '$10'
  },
  containerBottom: {
    flexGrow: 1,
    mt: '$10'
  },
  summaryContainer: {
    flexDirection: 'row'
  },
  buttonGroup: {
    flexDirection: 'row'
  },
  button: {
    height: '$9',
    width: '$25'
  }
};
